import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, ArrowUpRight } from "lucide-react";
import api from "../lib/api";

const STATUS = {
  green: { label: "Healthy", ring: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  amber: { label: "Needs attention", ring: "border-amber-200", bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  red: { label: "Action required", ring: "border-red-200", bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  info: { label: "Pending", ring: "border-black/10", bg: "bg-charcoal-50", text: "text-charcoal-500", dot: "bg-charcoal-300" },
};

// Public score-band explainer. Bands MUST stay in sync with backend/monitoring.py
// (SCORE_BAND_GREEN=90, SCORE_BAND_AMBER=70). Copy is written for donors, not operators.
const BANDS = [
  { key: "green", range: "90–100", label: "Healthy", desc: "Website, donation page, security, and forms are all working as expected." },
  { key: "amber", range: "70–89",  label: "Needs attention", desc: "Mostly working — but one or more items are worth reviewing this month." },
  { key: "red",   range: "Below 70", label: "Action required", desc: "One or more items need to be fixed for supporters to have a smooth experience." },
];

export default function Verify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/verify/${token}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [token]);

  const s = STATUS[data?.status] || STATUS.info;
  const dateStr = data?.last_checked
    ? new Date(data.last_checked).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-charcoal-50 flex flex-col items-center justify-center px-5 py-12" data-testid="verify-page">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white border border-black/5 shadow-premium overflow-hidden">
          <div className="px-7 py-5 border-b border-black/5 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900">Mission Watch Verified</span>
          </div>

          {error ? (
            <div className="px-7 py-14 text-center" data-testid="verify-notfound">
              <p className="text-charcoal-600 font-medium">Verification not found</p>
              <p className="text-sm text-charcoal-400 mt-1">This badge link is invalid or has expired.</p>
            </div>
          ) : !data ? (
            <div className="px-7 py-14 text-center text-sm text-charcoal-400 font-mono">Loading…</div>
          ) : (
            <div className="px-7 py-8 text-center">
              <p className="text-sm text-charcoal-500">This nonprofit is monitored by Mission Watch</p>
              <h1 className="text-2xl font-semibold tracking-tight text-charcoal-900 mt-1" data-testid="verify-org">
                {data.organization_name}
              </h1>

              <div className="mt-7 flex items-center justify-center">
                <div className={`w-32 h-32 rounded-full grid place-items-center ${s.bg} border-4 ${s.ring}`}>
                  <div className="text-center">
                    <div className="text-4xl font-semibold tracking-tighter text-charcoal-900" data-testid="verify-score">
                      {data.score ?? "—"}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Score</div>
                  </div>
                </div>
              </div>

              <div className={`mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full ${s.bg} border ${s.ring}`}>
                <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                <span className={`text-xs font-medium ${s.text}`}>{s.label}</span>
              </div>

              {data.verified && (
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-charcoal-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {data.checks_passing} of {data.checks_total} checks passing
                </div>
              )}
              {dateStr && (
                <p className="mt-3 text-xs font-mono text-charcoal-400">Last verified {dateStr}</p>
              )}
            </div>
          )}

          {/* Score-bands explainer — shown once we have data so donors understand the number. */}
          {data && !error && (
            <div className="px-7 pb-7 -mt-2" data-testid="verify-bands">
              <div className="border-t border-black/5 pt-5">
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400 text-center mb-4">
                  How the Mission Watch Score works
                </p>
                <ul className="space-y-3">
                  {BANDS.map((b) => {
                    const active = b.key === data.status;
                    const bs = STATUS[b.key];
                    return (
                      <li key={b.key}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${active ? `${bs.bg} ${bs.ring}` : "bg-white border-black/5"}`}
                        data-testid={`verify-band-${b.key}${active ? "-active" : ""}`}>
                        <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${bs.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-sm font-semibold text-charcoal-900">{b.range}</span>
                            <span className={`text-sm ${active ? bs.text : "text-charcoal-600"}`}>{b.label}</span>
                            {active && (
                              <span className={`ml-auto text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${bs.bg} ${bs.text} border ${bs.ring}`}>
                                This organization
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-charcoal-500 leading-relaxed">{b.desc}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-4 text-xs text-charcoal-500 leading-relaxed text-center">
                  A critical failure — the website going offline, the donation page breaking, or an
                  expired SSL certificate — marks the score red regardless of the number.
                </p>
              </div>
            </div>
          )}

          <div className="px-7 py-5 border-t border-black/5 flex items-center justify-between gap-3">
            <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-400">Powered by Mission Watch</span>
            <a href="/" className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline" data-testid="verify-cta">
              Get your nonprofit verified <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-charcoal-400">
          Independent monitoring of this organization&apos;s public digital presence.
        </p>
      </div>
    </div>
  );
}
